import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {BulkActionFooter} from 'src/components/bulk-tool/bulk-action-footer';
import {getPaginatedRowNumber} from 'src/components/main-column';
import {RequiredMark} from 'src/components/required-mark';
import {RowNumbered} from 'src/components/row-numbered';
import {WidthItem} from 'src/components/width-item';
import {useAccountManagerTypes} from 'src/hooks/dictionaries';
import {AccountTeamMemberSuggest} from 'src/modules/create-account/components/account-team-member-suggest';
import {getAccountName} from 'src/openapi/admin/apis/GetAccountNameApi';
import {useAppDispatch} from 'src/store/hooks';

import Button from '@pb/kit-component-button';
import CheckBox from '@pb/kit-component-checkbox';
import {ComboBox, ComboboxItem} from '@pb/kit-component-combo-box';
import {DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import {Info} from '@pb/kit-component-info';
import Label from '@pb/kit-component-label';
import Loading, {Types} from '@pb/kit-component-loading';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';
import {pluralize} from '@pb/kit-formatters';

import {TOOL_TITLE} from './constants';

import {updateAccountTeamMemberModalCellCenter} from './update-account-team-member-modal.module.styl';

import type {ProcessBulkAction} from 'src/components/bulk-tool/typedef';
import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {AccountManagerInfo} from 'src/openapi/admin/models/AccountManagerInfo';
import type {AccountManagerSearchResult} from 'src/openapi/admin/models/AccountManagerSearchResult';
import type {AssignAccountManagerRequest} from 'src/openapi/admin/models/AssignAccountManagerRequest';
import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {CheckBoxChangeData} from 'src/typedef';

type Props = {
    accountIds: Array<number>,
    onCloseModal: (submit: boolean) => void,
    processAction: ProcessBulkAction<AssignAccountManagerRequest[]>,
};

const TYPE_OVERRIDE_MESSAGE = 'Account Team Member with the selected role in the selected accounts will be updated';
const MODAL_DESCRIPTION = 'Assign an Account Team Member to the selected account[s] and set role type and alert' +
    ' options. Account Team Role can be customized for each account.';
const PRIMARY_MANAGER_OVERRIDE_MESSAGE = 'The primary account team member in the selected accounts will be updated';

const getLabeledRowId = (label: string, rowId: number) => `${label}-${rowId}`;

export const UpdateAccountTeamMemberModal: React.FC<Props> = ({accountIds, onCloseModal, processAction}) => {
    const apiDispatch = useAppDispatch();

    const [accountNames, setAccountNames] = useState<{[key: string]: string} | null>(null);
    const [accountManager, setAccountManager] = useState<AccountManagerSearchResult | null>(null);
    const [updates, setUpdates] = useState<{[key: string]: AccountManagerInfo}>(accountIds.reduce((acc, curr) => ({
        ...acc,
        [curr]: {id: -1, managerType: 'AM', primary: false, receiveAccountAlerts: false},
    }), {}));
    const [pagination, setPagination] = useState<Pagination>({currentPage: 1, pageSize: 5});

    const [highlightedRow, setHighlightedRow] = useState<number>(-1);
    const {filteredAccountManagerTypes} = useAccountManagerTypes();

    useEffect(() => {
        if (!accountNames) {
            apiDispatch(getAccountName({requestBody: accountIds})).then(response => {
                if (response.payload.success) {
                    setAccountNames(response.payload.data);
                }
            });
        }
    }, [accountNames, accountIds, apiDispatch]);

    useEffect(() => {
        setUpdates(prevUpdates => Object.fromEntries(
            Object.entries(prevUpdates).map(
                ([accountId, manager]) => [accountId, {
                    ...manager,
                    id: accountManager?.id || -1,
                }],
            ),
        ));
    }, [accountManager]);

    const onSetAccountManager = useCallback((value: AccountManagerSearchResult | undefined) => {
        setAccountManager(value || null);
        setUpdates(prevState => Object.entries(prevState).reduce((acc, curr) => ({
            ...acc,
            [curr[0]]: {...curr[1], email: value?.email, fullName: value?.fullName},
        }), {}));
    }, []);

    const onSetAccountManagerType = useCallback((managerType: string) => {
        setUpdates(
            Object.fromEntries(
                Object.entries(updates).map(
                    ([accountId, manager]) => [accountId, {...manager, managerType}],
                ),
            ),
        );
    }, [updates]);

    const onSetPrimary = useCallback(({event}: CheckBoxChangeData) => {
        setUpdates(
            Object.fromEntries(
                Object.entries(updates).map(
                    ([accountId, manager]) => [accountId, {
                        ...manager,
                        primary: event.currentTarget.checked,
                        receiveAccountAlerts: event.currentTarget.checked || manager.receiveAccountAlerts,
                    }],
                ),
            ),
        );
    }, [updates]);

    const onSetReceiveAccountAlerts = useCallback(({event}: CheckBoxChangeData) => {
        setUpdates(
            Object.fromEntries(
                Object.entries(updates).map(
                    ([accountId, manager]) => [accountId, {
                        ...manager,
                        receiveAccountAlerts: manager.primary || event.currentTarget.checked,
                    }],
                ),
            ),
        );
    }, [updates]);

    const onPaginationChange = useCallback((changedPagination: Pagination) => {
        const {currentPage, pageSize} = changedPagination;
        setPagination({currentPage, pageSize});
    }, []);

    const onUpdate = useCallback(async (options?: ActionOptions) => {
        if (accountManager && Object.keys(updates).length > 0) {
            return await processAction(Object.keys(updates).map(accountId => ({
                accountId: +accountId,
                managerId: accountManager.id,
                typeCode: updates[accountId].managerType,
                primary: updates[accountId].primary,
                receiveAlert: updates[accountId].receiveAccountAlerts,
            })), options);
        }
    }, [updates, accountManager, processAction]);

    const isCustomManagerTypeSettings = useMemo(
        () => {
            const managerTypes = Object.values(updates).map(({managerType}) => managerType);
            return managerTypes.some(managerType => managerTypes[0] !== managerType);
        },
        [updates],
    );

    const isCustomPrimarySettings = useMemo(
        () => {
            const primarySettings = Object.values(updates).map(update => update.primary);
            return primarySettings.some(isPrimary => primarySettings[0] !== isPrimary);
        },
        [updates],
    );

    const isCustomReceiveAccountAlertsSettings = useMemo(
        () => {
            const accountAlertsSettings = Object.values(updates).map(update => update.receiveAccountAlerts);
            return accountAlertsSettings.some(accountAlert => accountAlertsSettings[0] !== accountAlert);
        },
        [updates],
    );

    const firstUpdate = updates[Object.keys(updates)[0]];
    const isPrimaryChecked = firstUpdate.primary ? !isCustomPrimarySettings : undefined;
    const isAlertsDisabled = !isCustomPrimarySettings && firstUpdate.primary;
    const isAlertsChecked = isAlertsDisabled || (
        firstUpdate.receiveAccountAlerts && !isCustomReceiveAccountAlertsSettings
    );

    const {currentPage, pageSize} = pagination;

    return (
        <>
            <DialogHeader onClose={() => onCloseModal(false)}>
                {TOOL_TITLE}
            </DialogHeader>
            <DialogMain style={{padding: 24, maxWidth: 'fit-content'}}>
                <Group direction="v" gap="s">
                    {pluralize(MODAL_DESCRIPTION, accountIds.length)}
                    <Group gap="none">
                        <WidthItem width={460}>
                            <Label htmlFor="bulk-update-account-team-member-autosuggest">
                                <RequiredMark label={<strong>Account Team Member</strong>}/>
                            </Label>
                            <AccountTeamMemberSuggest

                                id="bulk-update-account-team-member-autosuggest"
                                selectedTeamMember={accountManager || undefined}
                                onSelect={onSetAccountManager}
                                placeholder="Select Team Member"
                            />
                        </WidthItem>
                        <WidthItem width={320}>
                            <Label htmlFor="bulk-update-account-team-member-role-select">
                                <Info type={{popover: TYPE_OVERRIDE_MESSAGE}}>Account Team Role</Info>
                            </Label>
                            <ComboBox
                                id="bulk-update-account-team-member-role-select"
                                placeholder="Select account team role"
                                onChange={onSetAccountManagerType}
                                value={isCustomManagerTypeSettings
                                    ? 'CUSTOM'
                                    : updates[Object.keys(updates)[0]].managerType}>
                                {[
                                    <ComboboxItem
                                        key="custom"
                                        disabled={true}
                                        caption="Custom"
                                        value="CUSTOM"
                                    />,
                                    ...filteredAccountManagerTypes.map(({code, description}) => (
                                        <ComboboxItem key={code} caption={description} value={code}/>
                                    )),
                                ]}
                            </ComboBox>
                        </WidthItem>
                        <WidthItem width={96}>
                            <Group direction="v" alignItems="center" gap="s">
                                <Label htmlFor="bulk-update-account-team-member-primary-checkbox">
                                    <Info type={{popover: PRIMARY_MANAGER_OVERRIDE_MESSAGE}}>Primary</Info>
                                </Label>
                                <CheckBox
                                    id="bulk-update-account-team-member-primary-checkbox"
                                    checked={isPrimaryChecked}
                                    indeterminate={isCustomPrimarySettings}
                                    onCheckBoxChange={onSetPrimary}
                                />
                            </Group>
                        </WidthItem>
                        <WidthItem width={100}>
                            <Group direction="v" alignItems="center" gap="s">
                                <Label htmlFor="bulk-update-account-team-member-alerts-checkbox">
                                    Account Alerts
                                </Label>
                                <CheckBox
                                    id="bulk-update-account-team-member-alerts-checkbox"
                                    disabled={isAlertsDisabled}
                                    checked={isAlertsChecked}
                                    indeterminate={isCustomReceiveAccountAlertsSettings}
                                    onCheckBoxChange={onSetReceiveAccountAlerts}
                                />
                            </Group>
                        </WidthItem>
                    </Group>
                    <div>
                        <Label>Customize Assignments</Label>
                        <Table>
                            <Head>
                                <Cell width={452}>
                                    <Group>
                                        <RowNumbered
                                            maxRowNumber={accountIds.length}
                                            placeholder={<strong>#</strong>}
                                        />
                                        <WidthItem width={230}>
                                            <Group>
                                                <strong>Account Name ({accountIds.length})</strong>
                                                {!accountNames && <Loading type={Types.ICON}/>}
                                            </Group>
                                        </WidthItem>
                                    </Group>
                                </Cell>
                                <Cell width={336}>Account Team Role</Cell>
                                <Cell width={80} className="text-align_center">Primary</Cell>
                                <Cell width={124} className="text-align_center">Account Alerts</Cell>
                            </Head>
                            <Body>
                                {accountIds
                                    .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                                    .map((id, index) => (
                                        <Row
                                            key={id}
                                            highlighted={highlightedRow === index}
                                            onMouseEnter={() => setHighlightedRow(index)}
                                            onMouseLeave={() => setHighlightedRow(-1)}>
                                            <Cell verticalAlign="middle">
                                                <Group>
                                                    <RowNumbered
                                                        maxRowNumber={accountIds.length}
                                                        placeholder={getPaginatedRowNumber({
                                                            currentPage,
                                                            pageSize,
                                                            index: index + 1,
                                                        })}
                                                    />
                                                    <WidthItem width={350}>
                                                        <EllipsisTextHint title={accountNames ? accountNames[id] : ''}>
                                                            {accountNames ? accountNames[id] : ''}
                                                        </EllipsisTextHint>
                                                    </WidthItem>
                                                </Group>
                                            </Cell>
                                            <Cell>
                                                <ComboBox
                                                    id={getLabeledRowId(
                                                        'bulk-update-account-team-member-role-select',
                                                        getPaginatedRowNumber({
                                                            currentPage,
                                                            pageSize,
                                                            index: index + 1,
                                                        }),
                                                    )}
                                                    value={updates[id].managerType}
                                                    onChange={managerType => setUpdates({
                                                        ...updates,
                                                        [id]: {...updates[id], managerType},
                                                    })}>
                                                    {filteredAccountManagerTypes.map(({code, description}) => (
                                                        <ComboboxItem
                                                            key={code}
                                                            caption={description}
                                                            value={code}
                                                        />
                                                    ))}
                                                </ComboBox>
                                            </Cell>
                                            <Cell verticalAlign="middle">
                                                <span className={updateAccountTeamMemberModalCellCenter}>
                                                    <CheckBox
                                                        id={getLabeledRowId(
                                                            'bulk-update-account-team-member-primary-checkbox',
                                                            getPaginatedRowNumber({
                                                                currentPage,
                                                                pageSize,
                                                                index: index + 1,
                                                            }),
                                                        )}
                                                        checked={updates[id].primary}
                                                        onCheckBoxChange={({event}) => setUpdates({
                                                            ...updates,
                                                            [id]: {
                                                                ...updates[id],
                                                                primary: event.currentTarget.checked,
                                                                receiveAccountAlerts: event.currentTarget.checked ||
                                                                    updates[id].receiveAccountAlerts,
                                                            },
                                                        })}
                                                    />
                                                </span>
                                            </Cell>
                                            <Cell verticalAlign="middle">
                                                <span className={updateAccountTeamMemberModalCellCenter}>
                                                    <CheckBox
                                                        id={getLabeledRowId(
                                                            'bulk-update-account-team-member-alerts-checkbox',
                                                            getPaginatedRowNumber({
                                                                currentPage,
                                                                pageSize,
                                                                index: index + 1,
                                                            }),
                                                        )}
                                                        checked={updates[id].receiveAccountAlerts}
                                                        disabled={updates[id].primary}
                                                        onCheckBoxChange={({event}) => setUpdates({
                                                            ...updates,
                                                            [id]: {
                                                                ...updates[id],
                                                                receiveAccountAlerts: event.currentTarget.checked,
                                                            },
                                                        })}
                                                    />
                                                </span>
                                            </Cell>
                                        </Row>
                                    ))}
                            </Body>
                        </Table>
                        <PaginationStripe
                            pageSize={pageSize}
                            totalCount={accountIds.length}
                            currentPage={currentPage}
                            onPaginationChange={onPaginationChange}
                            goToPage={true}
                            showSizeSwitcher={false}
                            sizeSwitcherPosition="RIGHT"
                            sizes={[5]}
                        />
                    </div>
                </Group>
            </DialogMain>
            <BulkActionFooter action="Update" onConfirm={onUpdate} disabled={!accountManager}>
                <Group>

                    <Button variant="Type.SECONDARY" onClick={() => onCloseModal(false)}>
                        Cancel
                    </Button>
                </Group>
            </BulkActionFooter>
        </>
    );
};
